import React, {useEffect, useState, useMemo, useRef, useCallback} from 'react';
import {Button, message, Tabs, Switch, Dropdown, Modal, Badge} from 'antd';
import type {MenuProps} from 'antd';
import {FilterOutlined, SortAscendingOutlined} from '@ant-design/icons';
import {useHashState} from 'utils/hashState';
import {PickupAppointmentInitialValue} from "./graphql/queries/getPickupAppointmentListing";
import {PickupAppointmentType} from "./graphql/queries/getPickupAppointmentListing";
import {usePickupAppointmentQuery} from "./hooks/usePickupAppointmentQuery";
import PickupAppointmentFilter from "./components/PickupAppointmentFilter";
import PickupAppointmentColumns from "./components/PickupAppointmentColumns";
import PickupAppointmentDrawer from "./components/PickupAppointmentDrawer";
import PickupTransferTaskModal from "./components/PickupTransferTaskModal";
import PickupTaskCreateDrawer from "./components/PickupTaskCreateDrawer";
import PickupTaskDetailDrawer from "../pickup-task/components/PickupTaskDetailDrawer";
import {PickupDataProvider} from "../pickup-task/contexts/PickupDataContext";
import PickupMergeAppointmentModal from "./components/PickupMergeAppointmentModal";
import PickupAppointmentCopyModal from "./components/PickupAppointmentCopyModal";
import AppointmentDownloadModal from "./components/AppointmentDownloadModal";
import {useMutation, useLazyQuery} from "@apollo/client";
import {TRANSFER_PICKUP_TASK_APPOINTMENT} from "../pickup-task/graphql/mutations/transferPickupTaskAppointment";
import {
    VALIDATE_PICKUP_APPOINTMENTS_COOPERATION_WAREHOUSE_STATUS
} from "./graphql/queries/validatePickupAppointmentsCooperationWarehouseStatus";
import {
    CONFIRM_PICKUP_APPOINTMENTS,
    ConfirmPickupAppointmentsResponse
} from "./graphql/mutations/confirmPickupAppointments";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import TablePaginationBar from '../../../components/TablePaginationBar';
import timezone from 'dayjs/plugin/timezone';
import {getCurrentTimeInTimezone, isBeforeTime} from '../../../utils/timezoneUtils';
import { useWarehouseContext } from "../../../contexts/WarehouseContext";
import {packageUnitFilterOptions} from "./types/PickupAppointmentType";
import {useTranslation} from "react-i18next";
import DateFilterItem from "../../delivery/components/DateFilterItem";
import {PickupSourceEnum} from "../../../enums/PickupSourceEnum";
import {
    buildPickupAppointmentDownloadFilters,
    useGeneratePickupAppointmentReport
} from "./hooks/useGeneratePickupAppointmentReport";

// 扩展 dayjs 支持时区功能
dayjs.extend(utc);
dayjs.extend(timezone);

const DEBUG_PICKUP_APPOINTMENTS_CONTENT = false;

interface PickupAppointmentsContentProps {
    onAppointmentsUpdate?: (appointments: PickupAppointmentType[]) => void;
    externalSelectedKeys?: React.Key[];
    onSelectionChange?: (selectedKeys: Map<any, any>) => void;
    onButtonHandlersUpdate?: (handlers: {
        handleCreateAppointment?: () => void;
        handleAddTask?: () => void;
        handleCreateTask?: () => void;
    }) => void;
    onIsPastDateUpdate?: (isPastDate: boolean) => void;
    onUseSystemRoutingUpdate?: (useSystemRouting: boolean) => void;
}

const PickupAppointmentsContent: React.FC<PickupAppointmentsContentProps> = ({
                                                                                 onAppointmentsUpdate,
                                                                                 externalSelectedKeys,
                                                                                 onSelectionChange,
                                                                                 onButtonHandlersUpdate,
                                                                                 onIsPastDateUpdate,
                                                                                 onUseSystemRoutingUpdate
                                                                             }) => {
    // 渲染计数
    const renderCount = useRef(0);
    renderCount.current += 1;

    // 追踪 props 变化
    const prevPropsRef = useRef({
        onAppointmentsUpdate,
        externalSelectedKeys,
        onSelectionChange,
        onButtonHandlersUpdate
    });

    const propsChanged = {
        onAppointmentsUpdate: prevPropsRef.current.onAppointmentsUpdate !== onAppointmentsUpdate,
        externalSelectedKeys: prevPropsRef.current.externalSelectedKeys !== externalSelectedKeys,
        onSelectionChange: prevPropsRef.current.onSelectionChange !== onSelectionChange,
        onButtonHandlersUpdate: prevPropsRef.current.onButtonHandlersUpdate !== onButtonHandlersUpdate
    };

    if (renderCount.current > 1) {
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log(`🔷 PickupAppointmentsContent render #${renderCount.current}`, propsChanged);
        }
    }

    prevPropsRef.current = {
        onAppointmentsUpdate,
        externalSelectedKeys,
        onSelectionChange,
        onButtonHandlersUpdate
    };

    const [detailVisible, setDetailVisible] = useState(false);
    const [appointmentId, setAppointmentId] = useState<number>();
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'detail'>('detail');

    // 初始化时检查是否有URL参数，如果有则构建初始状态
    const getInitialState = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const routeId = urlParams.get('routeId');
        const expectedPickupDate = urlParams.get('expectedPickupDate');
        const status = urlParams.get('status');
        const confirm = urlParams.get('confirm');

        // 没有URL参数时，使用默认值 - 默认显示已确认的预约单，且status为CREATED
        const today = dayjs().format('YYYY-MM-DD');
        let initialFilters = {...PickupAppointmentInitialValue};
        initialFilters.confirm=1
        initialFilters.status = 'CREATED';
            if (expectedPickupDate) {
                initialFilters.expectedPickupDate = expectedPickupDate;
            }else{
                initialFilters.expectedPickupDate = today;
           }
            return initialFilters;

    };

    const [tempFilters, setTempFilters] = useHashState(getInitialState());
    const [filters, setFilters] = useHashState(getInitialState());
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(1000);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    // 添加一个持久化的选择状态，用于跨分页保存选择
    const [persistentSelection, setPersistentSelection] = useState<React.Key[]>([]);

    // 保存所有选中的预约单数据，用于计算汇总信息
    const [selectedAppointments, setSelectedAppointments] = useState<Map<React.Key, PickupAppointmentType>>(new Map());

    // 保存勾选顺序的状态
    const [selectionOrder, setSelectionOrder] = useState<React.Key[]>([]);

    // 使用 ref 保存 selectedRowKeys 和 selectionOrder，避免在 useEffect 中形成循环依赖
    const selectedRowKeysRef = useRef(selectedRowKeys);
    const selectionOrderRef = useRef(selectionOrder);
    useEffect(() => {
        selectedRowKeysRef.current = selectedRowKeys;
        selectionOrderRef.current = selectionOrder;
    }, [selectedRowKeys, selectionOrder]);

    const [transferTaskModalVisible, setTransferTaskModalVisible] = useState(false);
    const [taskCreateDrawerVisible, setTaskCreateDrawerVisible] = useState(false);
    const [taskDetailDrawerVisible, setTaskDetailDrawerVisible] = useState(false);
    const [mergeAppointmentModalVisible, setMergeAppointmentModalVisible] = useState(false);
    const [copyAppointmentModalVisible, setCopyAppointmentModalVisible] = useState(false);
    const [appointmentFileName, setAppointmentFileName] = useState('');
    const [appointmentDownloadVisible, setAppointmentDownloadVisible] = useState(false);
    const [currentTaskId, setCurrentTaskId] = useState<string>('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [showUnscheduledOnly, setShowUnscheduledOnly] = useState(true);
    const [useSystemRouting, setUseSystemRouting] = useState(true);
    const [filterModalVisible, setFilterModalVisible] = useState(false); // 筛选弹窗显示/隐藏状态
    const [sortType, setSortType] = useState<string | null>(null); // 排序类型
    const [windowHeight, setWindowHeight] = useState(() => window.innerHeight);
    const { currentWarehouse: warehouse } = useWarehouseContext();
    const warehouseId = warehouse?.id!!;
    const {t} = useTranslation();
    const { submit: generateAppointmentReport } = useGeneratePickupAppointmentReport();
    // 用于标记 URL 参数是否已处理
    const urlParamsProcessed = useRef(false);

    // 添加一个状态来触发 canCreateAppointment 的重新计算
    const [currentTime, setCurrentTime] = useState(() => dayjs());

    // 每分钟更新一次当前时间，确保 canCreateAppointment 能够及时更新
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(dayjs());
        }, 60000); // 每分钟更新一次

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const handleResize = () => setWindowHeight(window.innerHeight);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // 检查是否可以创建预约（基于列表的 Pickup Date）
    const canCreateAppointment = useMemo(() => {
        // 如果没有warehouse信息或时区信息，默认允许创建
        if (!warehouse || !warehouse.timezone) {
            return true;
        }

        try {
            // 获取当前 warehouse 时区的时间
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse.timezone);

            // 获取列表筛选的 Pickup Date
            const listPickupDate = filters.expectedPickupDate || dayjs().format('YYYY-MM-DD');
            const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');

            // 如果列表的 Pickup Date 是过去的日期，不能创建预约
            if (dayjs(listPickupDate).isBefore(dayjs(today))) {
                return false;
            }

            // 如果列表的 Pickup Date 是未来日期，可以创建预约
            if (dayjs(listPickupDate).isAfter(dayjs(today))) {
                return true;
            }

            // 如果列表的 Pickup Date 是今天，检查截止时间限制
            // 如果没有设置appointmentDeadlineTime，则整天都可以创建预约
            if (!warehouse.appointmentDeadlineTime) {
                return true;
            }

            // 如果设置了appointmentDeadlineTime，检查是否超过截止时间
            const [hours, minutes] = warehouse.appointmentDeadlineTime.split(':').map(Number);
            const todayDeadlineInWarehouseZone = currentTimeInWarehouseZone
                .hour(hours)
                .minute(minutes)
                .second(0)
                .millisecond(0);

            return currentTimeInWarehouseZone.isBefore(todayDeadlineInWarehouseZone);
        } catch (error) {
            console.error('Error checking appointment deadline:', error);
            return true;
        }
    }, [warehouse, filters.expectedPickupDate, currentTime, refreshTrigger]);
// 检查选择的日期是否是今天以前的日期
    const isPastDate = useMemo(() => {
        // 如果没有warehouse信息或时区信息，默认不认为是过去日期
        if (!warehouse || !warehouse.timezone) {
            return false;
        }

        try {
            // 获取当前 warehouse 时区的时间
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse.timezone);

            // 获取列表筛选的 Pickup Date
            const listPickupDate = filters.expectedPickupDate || dayjs().format('YYYY-MM-DD');
            const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');

            // 如果列表的 Pickup Date 是过去的日期，返回 true
            return dayjs(listPickupDate).isBefore(dayjs(today));
        } catch (error) {
            console.error('Error checking if date is past:', error);
            return false;
        }
    }, [warehouse,filters.expectedPickupDate, currentTime]);


    // Transfer mutation
    const [transferAppointmentsToTask, {loading: transferLoading}] = useMutation(TRANSFER_PICKUP_TASK_APPOINTMENT);
    const [confirmAppointments, {loading: confirmLoading}] = useMutation<ConfirmPickupAppointmentsResponse>(CONFIRM_PICKUP_APPOINTMENTS);

    // Validation query for cooperation warehouse status
    const [validateCooperationWarehouseStatus, {loading: validationLoading}] = useLazyQuery(VALIDATE_PICKUP_APPOINTMENTS_COOPERATION_WAREHOUSE_STATUS);

    // 使用自定义hook获取数据
    const {
        appointmentData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables,
        pendingCount,
        confirmedCount,
        countsLoading,
        refetchCounts
    } = usePickupAppointmentQuery(filters, currentPage, pageSize, warehouseId, {fetchAllPages: true});

    // 手动拖拽排序状态
    const [manualSortedData, setManualSortedData] = useState<PickupAppointmentType[]>([]);
    const hasManualSortRef = useRef(false);
    
    // 添加渲染计数来追踪状态变化
    if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
        console.log(`📊 State - manualSortedData: ${manualSortedData.length}, hasManualSort: ${hasManualSortRef.current}`);
    }

    // 应用排序的数据 - 使用稳定的引用
    const sortedAppointmentData = useMemo(() => {
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log(`🟣 sortedAppointmentData useMemo - sortType: ${sortType}, appointmentData length: ${appointmentData?.length || 0}`);
        }

        // 如果没有数据，返回空数组（稳定引用）
        if (!appointmentData || appointmentData.length === 0) {
            return appointmentData || [];
        }

        // 如果没有排序类型，直接返回原数据
        if (!sortType) {
            return appointmentData;
        }

        const sorted = [...appointmentData];
        // 生成用于地址排序的键：忽略前置的数字/符号，从首个字母开始比较（不区分大小写）
        const getAddressSortKey = (address?: string): string => {
            const s = (address || '').trim();
            // 找到第一个字母的位置（A-Z 或 a-z）
            const match = s.match(/[A-Za-z]/);
            if (!match) return s.toLowerCase();
            const idx = match.index || 0;
            return s.substring(idx).toLowerCase();
        };
        
        switch (sortType) {
            case 'address-asc':
                return sorted.sort((a, b) => {
                    const ak = getAddressSortKey(a.addressLine1);
                    const bk = getAddressSortKey(b.addressLine1);
                    return ak.localeCompare(bk);
                });
            case 'address-desc':
                return sorted.sort((a, b) => {
                    const ak = getAddressSortKey(a.addressLine1);
                    const bk = getAddressSortKey(b.addressLine1);
                    return bk.localeCompare(ak);
                });
            case 'zipcode-asc':
                return sorted.sort((a, b) => 
                    (a.zipcode || '').localeCompare(b.zipcode || '')
                );
            case 'zipcode-desc':
                return sorted.sort((a, b) => 
                    (b.zipcode || '').localeCompare(a.zipcode || '')
                );
            default:
                return sorted;
        }
    }, [appointmentData, sortType]);
    
    // 当排序数据变化时，如果没有手动排序则更新
    // 使用 ref 来跟踪上一次的数据，避免空数组导致的无限循环
    const prevSortedDataRef = useRef<PickupAppointmentType[]>([]);
    useEffect(() => {
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log(`🟠 sortedAppointmentData useEffect - hasManualSort: ${hasManualSortRef.current}, length: ${sortedAppointmentData.length}`);
        }

        // 当打开创建任务抽屉或转移任务弹窗时，不应该因为数据刷新而清空选择
        const isOperating = taskCreateDrawerVisible || transferTaskModalVisible;

        // 如果数据为空，清空 manualSortedData 并重置手动排序标志，同时清空选中状态
        // 增加 !isOperating 判断，防止在打开弹窗时因为数据刷新导致空数据而清空选择
        if (sortedAppointmentData.length === 0 && !isOperating) {
            if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
                console.log(`🟠 sortedAppointmentData is empty, clearing manualSortedData and selection`);
            }
            setManualSortedData([]);
            hasManualSortRef.current = false;
            prevSortedDataRef.current = [];
            // 清空选中状态
            setSelectedRowKeys([]);
            setPersistentSelection([]);
            setSelectedAppointments(new Map());
            setSelectionOrder([]);
            // 通知地图清空选中状态
            if (onSelectionChange) {
                const clearMap = new Map();
                onSelectionChange(clearMap);
            }
            return;
        }

        // 检查数据是否真的变化了（通过比较第一项的 id）
        const dataChanged = sortedAppointmentData.length !== prevSortedDataRef.current.length ||
            (sortedAppointmentData.length > 0 && prevSortedDataRef.current.length > 0 &&
             sortedAppointmentData[0]?.id !== prevSortedDataRef.current[0]?.id);

        if (!hasManualSortRef.current && dataChanged) {
            if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
                console.log(`🟠 sortedAppointmentData useEffect - calling setManualSortedData`);
            }
            setManualSortedData(sortedAppointmentData);
            prevSortedDataRef.current = sortedAppointmentData;
        }
    }, [sortedAppointmentData, onSelectionChange, taskCreateDrawerVisible, transferTaskModalVisible]);

    // 处理手动排序
    const handleManualSort = useCallback((newData: PickupAppointmentType[]) => {
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log(`🔵 handleManualSort called with ${newData.length} items`);
        }
        hasManualSortRef.current = true;
        setManualSortedData(newData);

        // 根据新的数据顺序，更新选中项的序号
        // 只更新已选中项的序号，保持选中状态不变
        const newSelectionOrder: React.Key[] = [];
        const selectedSet = new Set(selectedRowKeys);

        // 按照新的数据顺序，收集选中项的ID
        newData.forEach((appointment) => {
            if (selectedSet.has(appointment.id)) {
                newSelectionOrder.push(appointment.id);
            }
        });

        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log(`🔵 handleManualSort - 更新序号，选中项数: ${newSelectionOrder.length}`);
        }
        setSelectionOrder(newSelectionOrder);

        // 通知地图组件更新序号（通过触发 onSelectionChange）
        // 关键：按照新的数据顺序构建 selectMap，确保 Map 的迭代顺序与列表顺序一致
        if (onSelectionChange && selectedRowKeys.length > 0) {
            const selectMap = new Map();
            // 按照新的数据顺序遍历，确保 Map 的插入顺序与列表顺序一致
            newData.forEach((appointment) => {
                selectMap.set(appointment.id, selectedSet.has(appointment.id));
            });
            if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
                console.log(`🔵 handleManualSort - 通知地图更新序号，选中项: ${newSelectionOrder.length}`);
            }
            onSelectionChange(selectMap);
        }

        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log(`🔵 handleManualSort - setManualSortedData completed`);
        }
    }, [selectedRowKeys, onSelectionChange]);

    // 最终显示的数据
    // 如果查询结果为空，无论是否有手动排序，都返回空数组
    const displayData = sortedAppointmentData.length === 0
        ? []
        : (hasManualSortRef.current ? manualSortedData : sortedAppointmentData);
    if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
        console.log(`🔵 displayData computed - hasManualSort: ${hasManualSortRef.current}, length: ${displayData.length}, manualSortedData: ${manualSortedData.length}, sortedAppointmentData: ${sortedAppointmentData.length}`);
    }
    const tableScrollY = useMemo(() => Math.max(240, windowHeight - 260), [windowHeight]);

    // 使用 ref 保存最新的 displayData，避免在回调中形成循环依赖
    const displayDataRef = useRef(displayData);
    useEffect(() => {
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log(`🟡 displayDataRef useEffect - updating ref with ${displayData.length} items`);
        }
        displayDataRef.current = displayData;
    }, [displayData]);

    // 更新处理选择变化的函数（取消跨分页缓存）
    const handleSelectChange = useCallback((newSelectedRowKeys: React.Key[], newSelectedRows: PickupAppointmentType[]) => {
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log(`🟢 handleSelectChange called - new selection: ${newSelectedRowKeys.length}, rows: ${newSelectedRows.length}`);
        }
        // 标记为手动排序模式
        hasManualSortRef.current = true;

        // 获取当前数据 - 使用 ref 避免循环依赖
        const currentData = [...displayDataRef.current];

        // 找出被取消勾选的项和新勾选的项
        const newSelectedSet = new Set(newSelectedRowKeys);
        const previousSelectedSet = new Set(selectedRowKeys);
        const newlyCheckedKeys = newSelectedRowKeys.filter(key => !previousSelectedSet.has(key));

        // 更新勾选顺序：保留之前的顺序，添加新勾选的，移除取消勾选的
        let newSelectionOrder = [...selectionOrder];

        // 移除取消勾选的项
        newSelectionOrder = newSelectionOrder.filter(key => newSelectedSet.has(key));

        // 添加新勾选的项到末尾
        newlyCheckedKeys.forEach(key => {
            if (!newSelectionOrder.includes(key)) {
                newSelectionOrder.push(key);
            }
        });

        // 更新勾选顺序状态
        setSelectionOrder(newSelectionOrder);

        // 创建一个 Map 用于快速查找数据
        const dataMap = new Map<React.Key, PickupAppointmentType>();
        currentData.forEach(item => {
            dataMap.set(item.id, item);
        });

        // 按勾选顺序排列已选中的项
        const selectedItems: PickupAppointmentType[] = [];
        newSelectionOrder.forEach(key => {
            const item = dataMap.get(key);
            if (item) {
                selectedItems.push(item);
            }
        });

        // 收集未选中的项
        const unselectedItems: PickupAppointmentType[] = [];
        currentData.forEach(item => {
            if (!newSelectedSet.has(item.id)) {
                unselectedItems.push(item);
            }
        });

        // 重新构建数据：按勾选顺序排列的已选中项在前面，未选中的排在后面
        const reorderedData: PickupAppointmentType[] = [
            ...selectedItems,
            ...unselectedItems
        ];

        // 更新排序后的数据
        setManualSortedData(reorderedData);

        // 只更新当前页面的选择
        setSelectedRowKeys(newSelectedRowKeys);

        // 更新选中的预约单数据（仅当前页）
        const newSelectedAppointments = new Map<React.Key, PickupAppointmentType>();
        newSelectedRows.forEach(appointment => {
            newSelectedAppointments.set(appointment.id, appointment);
        });
        setSelectedAppointments(newSelectedAppointments);

        // 通知外部选中状态变化 - 优化：只构建选中项的 Map，而不是遍历所有数据
        if (onSelectionChange) {
            const selectMap = new Map();
            const selectedSet = new Set(newSelectedRowKeys);
            // 只遍历当前显示的数据
            displayDataRef.current.forEach(appointment => {
                selectMap.set(appointment.id, selectedSet.has(appointment.id));
            });
            if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
                console.log(`🟢 handleSelectChange - calling onSelectionChange with ${selectMap.size} items`);
            }
            onSelectionChange(selectMap);
        }
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log(`🟢 handleSelectChange completed`);
        }
    }, [onSelectionChange, selectedRowKeys, selectionOrder]);

    // 监听外部选中状态变化（地图同步到列表）
    useEffect(() => {
        if (externalSelectedKeys === undefined || !sortedAppointmentData || sortedAppointmentData.length === 0) {
            return;
        }
        
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log('📋 收到地图选中变化:', externalSelectedKeys.length, '个');
        }

        // 标记为手动排序模式
        hasManualSortRef.current = true;

        setPersistentSelection(externalSelectedKeys);
        
        // 找出当前页面中应该被选中的项
        const currentPageKeys = sortedAppointmentData
            .filter(appointment => externalSelectedKeys.includes(appointment.id))
            .map(appointment => appointment.id);
        setSelectedRowKeys(currentPageKeys);
        
        // ⭐ 关键：更新选中的预约单数据，用于计算 Selected quantity of goods
        // 从当前页数据中获取完整的预约单信息（包含 expectedParcelCount 等字段）
        const newSelectedAppointments = new Map<React.Key, PickupAppointmentType>();
        sortedAppointmentData.forEach(appointment => {
            if (externalSelectedKeys.includes(appointment.id)) {
                newSelectedAppointments.set(appointment.id, appointment);
                if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
                    console.log(`  ✓ 添加到货量计算: ${appointment.appointmentNumber}, 数量: ${appointment.expectedParcelCount} ${appointment.expectedParcelUnit || 'PIECE'}`);
                }
            }
        });
        setSelectedAppointments(newSelectedAppointments);
        
        // 🔥 重要：触发排序逻辑，和手动勾选一样
        // 更新勾选顺序（使用 ref 获取当前值，避免循环依赖）
        const currentData = [...sortedAppointmentData];
        const newSelectedSet = new Set(externalSelectedKeys);
        const previousSelectedSet = new Set(selectedRowKeysRef.current);
        const newlyCheckedKeys = externalSelectedKeys.filter(key => !previousSelectedSet.has(key));

        // 更新勾选顺序：保留之前的顺序，添加新勾选的，移除取消勾选的
        let newSelectionOrder = [...selectionOrderRef.current];

        // 移除取消勾选的项
        newSelectionOrder = newSelectionOrder.filter(key => newSelectedSet.has(key));

        // 添加新勾选的项到末尾
        newlyCheckedKeys.forEach(key => {
            if (!newSelectionOrder.includes(key)) {
                newSelectionOrder.push(key);
            }
        });

        setSelectionOrder(newSelectionOrder);

        // 创建一个 Map 用于快速查找数据
        const dataMap = new Map<React.Key, PickupAppointmentType>();
        currentData.forEach(item => {
            dataMap.set(item.id, item);
        });

        // 按勾选顺序排列已选中的项
        const selectedItems: PickupAppointmentType[] = [];
        newSelectionOrder.forEach(key => {
            const item = dataMap.get(key);
            if (item) {
                selectedItems.push(item);
            }
        });

        // 收集未选中的项
        const unselectedItems: PickupAppointmentType[] = [];
        currentData.forEach(item => {
            if (!newSelectedSet.has(item.id)) {
                unselectedItems.push(item);
            }
        });

        // 重新构建数据：按勾选顺序排列的已选中项在前面，未选中的排在后面
        const reorderedData: PickupAppointmentType[] = [
            ...selectedItems,
            ...unselectedItems
        ];

        // 更新排序后的数据
        setManualSortedData(reorderedData);

        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log('📋 地图选中同步完成（含排序）:', {
                总选中: externalSelectedKeys.length,
                当前页显示: currentPageKeys.length,
                货量数据条数: newSelectedAppointments.size,
                勾选顺序: newSelectionOrder.length
            });
        }
    }, [externalSelectedKeys, sortedAppointmentData]); // 移除 selectedRowKeys 和 selectionOrder，因为它们在内部被更新

    // 计算选中项的汇总信息
    const selectedSummary = useMemo(() => {
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log('🧮 计算货量汇总，选中预约单数:', selectedAppointments.size);
        }
        
        if (selectedAppointments.size === 0) {
            return '';
        }

        // 按单位类型统计数量
        const unitCounts = new Map<string, number>();

        // 遍历所有选中的预约单，统计不同单位的数量
        selectedAppointments.forEach((appointment) => {
            const count = appointment.expectedParcelCount || 0;
            const unit = appointment.expectedParcelUnit || 'PIECE';

            // 按单位类型累加
            const currentCount = unitCounts.get(unit) || 0;
            unitCounts.set(unit, currentCount + count);
            
            if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
                console.log(`  - 预约单 ${appointment.appointmentNumber}: ${count} ${unit}`);
            }
        });

        // 构建显示字符串，格式为: "2 PIECES + 3 BAG + 4 BOX"
        const unitParts: string[] = [];

        // 按照packageUnitFilterOptions的顺序显示单位
        packageUnitFilterOptions.forEach(option => {
            const count = unitCounts.get(option.value);
            if (count && count > 0) {
                unitParts.push(`${count} ${option.label}`);
            }
        });

        // 如果没有任何单位有数量，返回空字符串
        if (unitParts.length === 0) {
            return '';
        }

        // 用 "+" 连接不同单位的数量
        const result = unitParts.join(' + ');
        if (DEBUG_PICKUP_APPOINTMENTS_CONTENT) {
            console.log('🧮 货量汇总结果:', result);
        }
        return result;
    }, [selectedAppointments]);

    const handleResetFilters = () => {
        const resetFilters = {
            ...PickupAppointmentInitialValue,
            expectedPickupDate: filters.expectedPickupDate, // 确保重置时也设置为今天
            status: 'CREATED', // 重置时设置为CREATED
            confirm: filters.confirm // 保留当前的confirm值，不重置
        };
        setTempFilters(resetFilters);
        setShowUnscheduledOnly(true); // 重置时开启开关
        // 在Modal中点击Reset时，只重置tempFilters，不应用
        // 需要用户点击Apply才会应用
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
        setFilterModalVisible(false); // 关闭弹窗
    };

    // 处理筛选弹窗取消
    const handleFilterCancel = () => {
        setTempFilters(filters); // 恢复为当前筛选条件
        setFilterModalVisible(false);
    };

    // 监听日期变化，自动清空选择状态
    useEffect(() => {
        // 当日期变化时，清空选择状态，避免将不同日期的预约单关联
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());
        setSelectionOrder([]); // 清空勾选顺序

        // 通知地图清空选中状态 - 优化：只在有数据时才通知
        if (onSelectionChange && displayData.length > 0) {
            const clearMap = new Map();
            displayData.forEach(appointment => {
                clearMap.set(appointment.id, false);
            });
            onSelectionChange(clearMap);
        }
    }, [filters.expectedPickupDate]);

    // 监听显式刷新触发器执行查询：
    // - filters/currentPage/pageSize 变化已由 usePickupAppointmentQuery 内部 useQuery 通过 variables 自动重查，不再在此重复 refetch
    // - 仅在 refreshTrigger 变化（手动刷新按钮等业务路径）时主动 refetch
    const refetchInitialMountRef = useRef(true);
    useEffect(() => {
        if (refetchInitialMountRef.current) {
            refetchInitialMountRef.current = false;
            return;
        }
        refetch(buildQueryVariables());
        refetchCounts();
    }, [refreshTrigger]);

    // 使用 ref 保存上一次的 appointmentData ID 集合，用于检测数据是否真的变化了
    const prevAppointmentIdsRef = useRef<Set<number>>(new Set());

    // 锁住 effect 内读取的"非数据"值（callback 引用 / 抽屉弹窗开关 / 已确认 Tab 标志），
    // 避免它们变化时触发整段 effect 重跑，导致地图收到重复的 appointmentData 推送
    const notifyMapHandlersRef = useRef({
        onAppointmentsUpdate,
        onSelectionChange,
        taskCreateDrawerVisible,
        transferTaskModalVisible,
        confirmFilter: filters.confirm,
    });
    useEffect(() => {
        notifyMapHandlersRef.current = {
            onAppointmentsUpdate,
            onSelectionChange,
            taskCreateDrawerVisible,
            transferTaskModalVisible,
            confirmFilter: filters.confirm,
        };
    });

    // 当预约单数据更新时，通知父组件（使用原始数据，不包含排序）
    useEffect(() => {
        const {
            onAppointmentsUpdate: notifyUpdate,
            onSelectionChange: notifySelectionChange,
            taskCreateDrawerVisible: drawerOpen,
            transferTaskModalVisible: modalOpen,
            confirmFilter,
        } = notifyMapHandlersRef.current;

        if (notifyUpdate) {
            console.log('📋 列表通知地图更新数据:', appointmentData.length, '条');

            // 检测数据是否真的变化了（不仅仅是分页）
            const currentIds = new Set(appointmentData.map(apt => apt.id));
            const prevIds = prevAppointmentIdsRef.current;

            // 如果数据集合发生变化（不是简单的分页），清空选择状态和序号
            const dataChanged = currentIds.size !== prevIds.size ||
                Array.from(currentIds).some(id => !prevIds.has(id)) ||
                Array.from(prevIds).some(id => !currentIds.has(id));

            // 当打开创建任务抽屉或转移任务弹窗时，不应该因为数据刷新而清空选择
            const isOperating = drawerOpen || modalOpen;

            if (dataChanged && prevIds.size > 0 && !isOperating) {
                console.log('📋 检测到数据集合变化，清空选择状态和序号');
                setSelectedRowKeys([]);
                setPersistentSelection([]);
                setSelectedAppointments(new Map());
                setSelectionOrder([]);

                // 通知地图清空选中状态
                if (notifySelectionChange) {
                    const clearMap = new Map();
                    appointmentData.forEach(appointment => {
                        clearMap.set(appointment.id, false);
                    });
                    notifySelectionChange(clearMap);
                }
            }

            // 已确认 Tab 下：仅保留当前数据中仍为 CREATED 的选中项，避免陈旧选中导致 sourceTaskId=null 却提交已排线预约单
            // 增加 !isOperating 判断，防止在打开弹窗时因为数据刷新导致误删选中
            if (confirmFilter === 1 && selectedRowKeysRef.current.length > 0 && !isOperating) {
                const statusById = new Map(appointmentData.map((apt) => [apt.id, apt.status ?? ''] as [number, string]));
                const kept = selectedRowKeysRef.current.filter((id) => statusById.get(Number(id)) === 'CREATED');
                if (kept.length !== selectedRowKeysRef.current.length) {
                    console.log('📋 已确认 Tab：移除不再为 CREATED 的选中项', {
                        原选中数: selectedRowKeysRef.current.length,
                        保留数: kept.length
                    });
                    setSelectedRowKeys(kept);
                    setPersistentSelection(kept);
                    const keptSet = new Set(kept);
                    const newSelectedAppointments = new Map<React.Key, PickupAppointmentType>();
                    appointmentData.forEach((apt) => {
                        if (keptSet.has(apt.id)) newSelectedAppointments.set(apt.id, apt);
                    });
                    setSelectedAppointments(newSelectedAppointments);
                    setSelectionOrder(selectionOrderRef.current.filter((k) => keptSet.has(k)));
                    if (notifySelectionChange) {
                        const selectMap = new Map<number, boolean>();
                        appointmentData.forEach((apt) => {
                            selectMap.set(apt.id, keptSet.has(apt.id));
                        });
                        notifySelectionChange(selectMap);
                    }
                }
            }

            // 更新 ref
            prevAppointmentIdsRef.current = currentIds;

            // 通知地图更新数据
            notifyUpdate(appointmentData);
        }
    }, [appointmentData]);


    const showAppointmentDetail = (record: PickupAppointmentType) => {
        setAppointmentId(record.id);
        setDrawerMode('detail');
        setDetailVisible(true);
    };

    // 处理分页变化 - 清空选择状态
    const handlePageChange = useCallback((page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
        // 分页变化时清空选择
        setSelectedRowKeys([]);
        setSelectedAppointments(new Map());
        setPersistentSelection([]);
        setSelectionOrder([]); // 清空勾选顺序

        // 通知地图清空选中状态 - 优化：延迟到下一帧执行，避免阻塞UI
        if (onSelectionChange) {
            requestAnimationFrame(() => {
                const clearMap = new Map();
                displayData.forEach(appointment => {
                    clearMap.set(appointment.id, false);
                });
                onSelectionChange(clearMap);
            });
        }
    }, [onSelectionChange, displayData]);

    // 切换 Tab：待确认 / 已确认
    const handleTabSwitch = useCallback((key: string) => {
        const confirmValue = key === 'confirmed' ? 1 : 0;
        const newFilters = {...filters, confirm: confirmValue};
        setFilters(newFilters);
        setTempFilters(newFilters);
        setCurrentPage(1);
        // 清空选择项
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());
        setSelectionOrder([]); // 清空勾选顺序

        // 通知地图清空选中状态
        if (onSelectionChange && displayData.length > 0) {
            const clearMap = new Map();
            displayData.forEach(appointment => {
                clearMap.set(appointment.id, false);
            });
            onSelectionChange(clearMap);
        }
    }, [filters, onSelectionChange, displayData]);

    const handleConfirmAppointment = () => {
        // 仅允许在 Pending 列表中进行确认
        if (filters.confirm !== 0) {
            message.warning('Only appointments in Pending Confirmation can be confirmed.');
            return;
        }

        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one appointment to confirm.');
            return;
        }

        const appointmentIds = selectedRowKeys.map((key: React.Key) => Number(key));

        confirmAppointments({variables: {appointmentIds}})
            .then(() => {
                message.success('Appointments confirmed successfully');
                // 刷新列表
                setRefreshTrigger(prev => prev + 1);
                // 刷新数量统计
                refetchCounts();
                // 清空选择
                setSelectedRowKeys([]);
                setPersistentSelection([]);
                setSelectedAppointments(new Map());
                setSelectionOrder([]); // 清空勾选顺序
            })
            .catch(() => {
                message.error('Failed to confirm appointments');
            });
    };

    const handleCreateAppointment = useCallback(() => {
        console.log(`🟢 handleCreateAppointment called`);
        // 检查是否超过截止时间，如果超过则显示错误提示并禁止打开抽屉
        if (!canCreateAppointment) {
            const currentTimeInWarehouseZone = getCurrentTimeInTimezone(warehouse?.timezone || 'UTC');
            const listPickupDate = filters.expectedPickupDate || dayjs().format('YYYY-MM-DD');
            const today = currentTimeInWarehouseZone.format('YYYY-MM-DD');

            let errorMessage = '';
            if (dayjs(listPickupDate).isBefore(dayjs(today))) {
                errorMessage = `Cannot create appointment for ${listPickupDate}: this date is in the past. Please select today or a future date.`;
            } else if (dayjs(listPickupDate).isSame(dayjs(today))) {
                if (warehouse?.appointmentDeadlineTime) {
                    errorMessage = `Cannot create appointment for ${listPickupDate}: current time (${currentTimeInWarehouseZone.format('HH:mm')}) has passed the appointment deadline (${warehouse.appointmentDeadlineTime}) in ${warehouse.timezone}. Please select a future date.`;
                } else {
                    errorMessage = `Cannot create appointment for ${listPickupDate}. Please select a valid date.`;
                }
            } else {
                errorMessage = `Cannot create appointment for ${listPickupDate}. Please select a valid date.`;
            }

            message.error(errorMessage);
            return; // 禁止打开抽屉
        }

        // 继续创建预约流程
        setAppointmentId(undefined);
        setDrawerMode('create');
        setDetailVisible(true);
    }, [canCreateAppointment, warehouse, filters.expectedPickupDate]);

    // 处理任务详情显示
    const handleShowTaskDetail = (taskId: string) => {
        if (!taskId || taskId.trim() === '') {
            message.error('Invalid task ID');
            return;
        }
        setCurrentTaskId(taskId);
        setTaskDetailDrawerVisible(true);
    };

    // 更新创建任务的处理函数
    const handleCreateTask = useCallback(async () => {
        console.log(`🟢 handleCreateTask called`);
        if (!filters.expectedPickupDate) {
            message.error('Please select a pickup date first.');
            return;
        }
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one appointment to create a task.');
            return;
        }

        // 验证选中预约单的合作仓库状态
        if (selectedRowKeys.length === 0) {
            return;
        }

        try {
            const appointmentIds = selectedRowKeys.map((key: React.Key) => Number(key));
            const result = await validateCooperationWarehouseStatus({
                variables: {ids: appointmentIds}
            });

            const validationResult = result.data?.validatePickupAppointmentsCooperationWarehouseStatus;

            if (!validationResult?.success) {
                const errorMessage = validationResult?.message || 'Validation failed: cooperation warehouse status is invalid';
                message.error(errorMessage);
                return;
            }
        } catch (error: any) {
            console.error('Validation error:', error);
            message.error('Failed to validate appointments');
            return;
        }

        setTaskCreateDrawerVisible(true);
    }, [filters.expectedPickupDate, selectedRowKeys, validateCooperationWarehouseStatus]);

    // 获取当前选择的 PickupDate，如果没有选择则使用今天的日期
    const getCurrentPickupDate = () => {
        return filters.expectedPickupDate || dayjs().format('YYYY-MM-DD');
    };

    // 验证选中预约单的合作仓库状态
    const validateSelectedAppointments = async (): Promise<boolean> => {
        if (selectedRowKeys.length === 0) {
            return false;
        }

        try {
            const appointmentIds = selectedRowKeys.map((key: React.Key) => Number(key));
            const result = await validateCooperationWarehouseStatus({
                variables: {ids: appointmentIds}
            });

            const validationResult = result.data?.validatePickupAppointmentsCooperationWarehouseStatus;

            // 检查验证结果
            if (!validationResult?.success) {
                // 验证失败，显示具体的错误信息
                const errorMessage = validationResult?.message || 'Validation failed: cooperation warehouse status is invalid';
                message.error(errorMessage);
                return false;
            }

            return true;
        } catch (error: any) {
            console.error('Transfer error:', error);

            // 提取详细的错误信息
            let errorMessage = 'Failed to transfer appointments';

            message.error(errorMessage);
            return false;
        }
    };

    const handleDrawerSuccess = () => {
        refetch(buildQueryVariables());
        // 刷新数量统计
        refetchCounts();
        // 清空选择
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());
        setSelectionOrder([]); // 清空勾选顺序
    };

    // 处理任务创建成功
    const handleTaskCreateSuccess = () => {
        refetch(buildQueryVariables());
        // 刷新数量统计
        refetchCounts();

        // 清空选择
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());
        setSelectionOrder([]); // 清空勾选顺序

        // 关闭抽屉
        setTaskCreateDrawerVisible(false);
    };

    // 处理任务详情成功
    const handleTaskDetailSuccess = () => {
        refetch(buildQueryVariables());
        setTaskDetailDrawerVisible(false);
        setRefreshTrigger(prev => prev + 1);
        // 刷新数量统计
        refetchCounts();
    };

    // 打开转移任务模态框
    const handleAddTask = useCallback(async () => {
        console.log(`🟢 handleAddTask called`);
        if (!filters.expectedPickupDate) {
            message.error('Please select a pickup date first.');
            return;
        }
        if (selectedRowKeys.length === 0) {
            message.warning('Please select at least one appointment to transfer.');
            return;
        }

        // 验证选中预约单的合作仓库状态
        try {
            const appointmentIds = selectedRowKeys.map((key: React.Key) => Number(key));
            const result = await validateCooperationWarehouseStatus({
                variables: {ids: appointmentIds}
            });

            const validationResult = result.data?.validatePickupAppointmentsCooperationWarehouseStatus;

            if (!validationResult?.success) {
                const errorMessage = validationResult?.message || 'Validation failed: cooperation warehouse status is invalid';
                message.error(errorMessage);
                return;
            }
        } catch (error: any) {
            console.error('Validation error:', error);
            message.error('Failed to validate appointments');
            return;
        }

        // 获取当前任务ID（如果有）
        const taskId = '';
        setCurrentTaskId(taskId);

        // 打开模态框
        setTransferTaskModalVisible(true);
    }, [filters.expectedPickupDate, selectedRowKeys, validateCooperationWarehouseStatus]);

    // 处理任务转移确认
    const handleTransferConfirm = async (targetTaskId: string) => {
        try {
            // 使用当前选择的ID
            const appointmentIds = selectedRowKeys.map((key: React.Key) => key.toString());

            await transferAppointmentsToTask({
                variables: {
                    appointmentIds,
                    targetTaskId: targetTaskId,
                    sourceTaskId: null
                }
            });

            // 成功后直接关闭模态框
            setTransferTaskModalVisible(false);

            // 刷新列表
            setRefreshTrigger(prev => prev + 1);
            // 刷新数量统计
            refetchCounts();

            // 清空选择
            setSelectedRowKeys([]);
            setPersistentSelection([]);
            setSelectedAppointments(new Map());
            setSelectionOrder([]); // 清空勾选顺序

            // 显示成功消息
            message.success('Appointments transferred successfully');
        } catch (error) {
            message.error('Failed to transfer appointments');
            console.error('Transfer error:', error);
        }
    };

    // 处理合并预约
    const handleMergeAppointment = () => {
        if (!filters.expectedPickupDate) {
            message.error('Please select a pickup date first.');
            return;
        }
        if (selectedRowKeys.length < 2) {
            message.warning('Please select at least 2 appointments to merge.');
            return;
        }

        // 校验选中的预约单是否属于同一个客户 (channelId相同)
        const selectedAppointmentsArray = Array.from(selectedAppointments.values());
        const firstChannelId = selectedAppointmentsArray[0].channelId;
        const isSameChannel = selectedAppointmentsArray.every(appointment =>
            appointment.channelId === firstChannelId
        );

        if (!isSameChannel) {
            message.warning('Please select appointments from the same customer.');
            return;
        }

        setMergeAppointmentModalVisible(true);
    };

    // 处理合并预约成功
    const handleMergeSuccess = () => {
        // 刷新列表
        setRefreshTrigger(prev => prev + 1);
        // 刷新数量统计
        refetchCounts();

        // 清空选择
        setSelectedRowKeys([]);
        setPersistentSelection([]);
        setSelectedAppointments(new Map());
        setSelectionOrder([]); // 清空勾选顺序

        // 关闭模态框
        setMergeAppointmentModalVisible(false);
    };

    // 处理复制预约
    const handleCopyAppointment = () => {
        setCopyAppointmentModalVisible(true);
    };

    // 处理复制预约成功
    const handleCopySuccess = () => {
        // 刷新列表
        setRefreshTrigger(prev => prev + 1);
        // 刷新数量统计
        refetchCounts();

        // 关闭模态框
        setCopyAppointmentModalVisible(false);
    };

    // 下载处理函数 - 统一走下载中心异步报表流程
    const handleDownloadAppointments = async () => {
        if (!warehouse?.id) {
            message.error(t('pickup.appointment.messages.pleaseSelectWarehouse') || 'Please select a warehouse first');
            return;
        }

        const fileName = await generateAppointmentReport(
            buildPickupAppointmentDownloadFilters(filters, warehouse.id),
            Intl.DateTimeFormat().resolvedOptions().timeZone
        );
        if (fileName) {
            setAppointmentFileName(fileName);
            setAppointmentDownloadVisible(true);
        }
    };

    // 排序菜单选项
    const sortMenuItems: MenuProps['items'] = [
        {
            key: 'address-asc',
            label: 'Address: A → Z',
        },
        {
            key: 'address-desc',
            label: 'Address: Z → A',
        },
        {
            key: 'zipcode-asc',
            label: 'Zipcode: Low → High',
        },
        {
            key: 'zipcode-desc',
            label: 'Zipcode: High → Low',
        },
        {
            type: 'divider',
        },
        {
            key: 'clear',
            label: 'Clear Sort',
        },
    ];

    // 处理排序菜单点击
    const handleSortMenuClick: MenuProps['onClick'] = ({ key }) => {
        if (key === 'clear') {
            setSortType(null);
        } else {
            setSortType(key);
        }
    };

    // 计算筛选条件数量（排除默认值和空值）
    const activeFilterCount = useMemo(() => {
        let count = 0;
        const initialValue = PickupAppointmentInitialValue;
        
        // 检查每个筛选字段是否有值（排除默认值）
        // channelId
        if (filters.channelId !== undefined && filters.channelId !== initialValue.channelId) {
            count++;
        }
        
        // cooperationWarehouseName
        if (filters.cooperationWarehouseName !== undefined && 
            filters.cooperationWarehouseName !== initialValue.cooperationWarehouseName &&
            filters.cooperationWarehouseName.trim() !== '') {
            count++;
        }
        
        // routeIdList
        if (filters.routeIdList !== undefined && 
            filters.routeIdList !== initialValue.routeIdList &&
            Array.isArray(filters.routeIdList) && filters.routeIdList.length > 0) {
            count++;
        }
        
        // zipcodeList
        if (filters.zipcodeList !== undefined && 
            filters.zipcodeList !== initialValue.zipcodeList &&
            Array.isArray(filters.zipcodeList) && filters.zipcodeList.length > 0) {
            count++;
        }
        
        // address
        if (filters.address !== undefined && 
            filters.address !== initialValue.address &&
            filters.address.trim() !== '') {
            count++;
        }
        
        // status 单选（兼容旧参数，排除默认 CREATED）
        if (filters.status !== undefined && filters.status !== 'CREATED') {
            count++;
        }
        // statusList 多选
        if (filters.statusList !== undefined &&
            Array.isArray(filters.statusList) &&
            filters.statusList.length > 0) {
            count++;
        }
        // cooperationWarehouseIdList 站点ID多选
        if (filters.cooperationWarehouseIdList !== undefined &&
            Array.isArray(filters.cooperationWarehouseIdList) &&
            filters.cooperationWarehouseIdList.length > 0) {
            count++;
        }
        // cityList 城市多选
        if (filters.cityList !== undefined &&
            Array.isArray(filters.cityList) &&
            filters.cityList.length > 0) {
            count++;
        }
        
        // source（单选，兼容旧参数）
        if (filters.source !== undefined && filters.source !== initialValue.source) {
            count++;
        }
        // sourceList（多选）
        if (filters.sourceList !== undefined &&
            Array.isArray(filters.sourceList) &&
            filters.sourceList.length > 0) {
            count++;
        }
        
        // expectedParcelUnit
        if (filters.expectedParcelUnit !== undefined && 
            filters.expectedParcelUnit !== initialValue.expectedParcelUnit) {
            count++;
        }
        
        // appointmentNumberList
        if (filters.appointmentNumberList !== undefined && 
            filters.appointmentNumberList !== initialValue.appointmentNumberList &&
            Array.isArray(filters.appointmentNumberList) && filters.appointmentNumberList.length > 0) {
            count++;
        }
        
        // isNew
        if (filters.isNew !== undefined && filters.isNew !== initialValue.isNew) {
            count++;
        }
        
        // continuousFailure
        if (filters.continuousFailure !== undefined && 
            filters.continuousFailure !== initialValue.continuousFailure) {
            count++;
        }
        
        // trackingNumber
        if (filters.trackingNumber !== undefined && 
            filters.trackingNumber !== initialValue.trackingNumber &&
            filters.trackingNumber.trim() !== '') {
            count++;
        }
        
        // pickupSerialNo
        if (filters.pickupSerialNo !== undefined && 
            filters.pickupSerialNo !== initialValue.pickupSerialNo &&
            filters.pickupSerialNo.trim() !== '') {
            count++;
        }
        
        // vehicleTypeIdList（多选）
        if (filters.vehicleTypeIdList !== undefined &&
            Array.isArray(filters.vehicleTypeIdList) &&
            filters.vehicleTypeIdList.length > 0) {
            count++;
        }
        
        // failureReasonCode
        if (filters.failureReasonCode !== undefined && 
            filters.failureReasonCode !== initialValue.failureReasonCode) {
            count++;
        }
        
        // driverId
        if (filters.driverId !== undefined && filters.driverId !== initialValue.driverId) {
            count++;
        }
        
        return count;
    }, [filters]);

    // 处理Show Unscheduled Only开关切换
    const handleUnscheduledToggle = (checked: boolean) => {
        setShowUnscheduledOnly(checked);
        // 重置页码为1
        setCurrentPage(1);
        
        if (checked) {
            const newFilters = { ...tempFilters, status: 'CREATED', statusList: undefined };
            setTempFilters(newFilters);
            setFilters(newFilters);
        } else {
            const newFilters = { ...tempFilters, status: undefined, statusList: undefined };
            setTempFilters(newFilters);
            setFilters(newFilters);
        }
    };

    const handleSystemRoutingToggle = (checked: boolean) => {
        setUseSystemRouting(checked);
    };

    // 暴露按钮处理函数给父组件
    useEffect(() => {
        if (onButtonHandlersUpdate) {
            onButtonHandlersUpdate({
                handleCreateAppointment,
                handleAddTask,
                handleCreateTask
            });
        }
    }, [onButtonHandlersUpdate, handleCreateAppointment, handleAddTask, handleCreateTask]);

    // 暴露 isPastDate 给父组件
    useEffect(() => {
        if (onIsPastDateUpdate) {
            onIsPastDateUpdate(isPastDate);
        }
    }, [onIsPastDateUpdate, isPastDate]);

    // 暴露 useSystemRouting 给父组件
    useEffect(() => {
        if (onUseSystemRoutingUpdate) {
            onUseSystemRoutingUpdate(useSystemRouting);
        }
    }, [onUseSystemRoutingUpdate, useSystemRouting]);

    return (
        <div className="h-full flex flex-col bg-white rounded-lg shadow-sm">
            {/* 顶部筛选区域 */}
            <div className="px-6 pt-4 pb-2 flex-shrink-0">
                {/* 显示日期和标题 */}
                {/*<div className="mb-3">*/}
                {/*    <h2 className="text-lg font-semibold text-gray-800">*/}
                {/*        {filters.expectedPickupDate || dayjs().format('YYYY-MM-DD')} {t('pickup.appointment.confirmedAppointment')}*/}
                {/*    </h2>*/}
                {/*</div>*/}

                {/* 筛选区域 */}
                <div className="mb-3 flex items-center">
                    <div className="flex items-center gap-6">
                        {/* 揽收日期选择器 */}
                        <DateFilterItem
                            label={t('pickup.appointment.filters.pickupDate')}
                            value={filters.expectedPickupDate ? dayjs(filters.expectedPickupDate) : dayjs()}
                            onChange={(date) => {
                                // 确保日期不能为空，如果为空则设置为今天
                                const selectedDate = date || dayjs();
                                const newPickupDate = selectedDate.format('YYYY-MM-DD');

                                // 同时更新 filters（触发查询）和 tempFilters（保持同步）
                                setFilters({
                                    ...filters,
                                    expectedPickupDate: newPickupDate
                                });
                                setTempFilters({
                                    ...tempFilters,
                                    expectedPickupDate: newPickupDate
                                });
                            }}
                            className="!flex-row !items-center"
                            spanClassName="!font-medium !mb-0 !mr-3 whitespace-nowrap"
                        />
                        {/* Show Unscheduled Only 开关 */}
                        <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-700 mr-3">{t('pickup.appointment.showUnscheduledOnly')}:</span>
                            <Switch
                                checked={showUnscheduledOnly}
                                onChange={handleUnscheduledToggle}
                            />
                        </div>
                        {/* 使用系统排线 开关 */}
                        <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-700 mr-3">{t('pickup.appointment.useSystemRouting')}:</span>
                            <Switch
                                checked={useSystemRouting}
                                onChange={handleSystemRoutingToggle}
                            />
                        </div>
                    </div>
                </div>

                {/* 已选择货量显示 */}
                <div className="mt-2 mb-3 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        <p>{t('pickup.appointment.selectedAppointmentCount')}: {selectedAppointments.size}, {t('pickup.appointment.selectedQuantityOfGoods')}: {selectedSummary}</p>
                    </div>

                    {/* 筛选和排序按钮 */}
                    <div className="flex items-center gap-2">
                        <Badge count={activeFilterCount} showZero={false} offset={[-8, 8]}>
                            <Button
                                icon={<FilterOutlined />}
                                onClick={() => {
                                    setTempFilters(filters); // 打开弹窗时同步当前筛选条件
                                    setFilterModalVisible(true);
                                }}
                            >
                                {t('pickup.appointment.buttons.filter')}
                            </Button>
                        </Badge>
                        <Dropdown
                            menu={{ items: sortMenuItems, onClick: handleSortMenuClick }}
                            trigger={['click']}
                        >
                            <Button
                                icon={<SortAscendingOutlined />}
                                type={sortType ? 'primary' : 'default'}
                            >
                                {t('pickup.appointment.buttons.sort')}
                            </Button>
                        </Dropdown>
                    </div>
                </div>


            </div>

            {/* 中间列表区域 - 可滚动 */}
            <div className="flex-1 overflow-auto px-6">
                {/* 预约单列表 */}
                <PickupAppointmentColumns
                    appointmentData={displayData}
                    showAppointmentDetail={showAppointmentDetail}
                    showTaskDetail={handleShowTaskDetail}
                    onSelectChange={handleSelectChange}
                    selectedRowKeys={selectedRowKeys}
                    loading={loading}
                    onRefresh={() => setRefreshTrigger(prev => prev + 1)}
                    confirm={filters.confirm}
                    scrollY={tableScrollY}
                    onManualSort={handleManualSort}
                    enableDragSort={!!onSelectionChange}
                    useSystemRouting={useSystemRouting}
                    customColumnOrder={[
                        'index',
                        'appointmentNumber',
                        'address',
                        'warehouseLatitude',
                        'warehouseLongitude',
                        'cooperationWarehouseLatitude',
                        'cooperationWarehouseLongitude',
                        'zipcode',
                        'route',
                        'vehicleType',
                        'status',
                        'cooperationWarehouseName',
                        'expectedPickupTime',
                        'expectedParcelCount',
                        'expectedParcelUnit',
                        'source',
                        'isNew',
                        'continuousFailure',
                        'channelName',
                        'taskNumber',
                        'updatedAt',
                        'actions'
                    ]}
                />
            </div>

            {/* 分页栏 - 固定在底部操作栏上方 */}
            <div className="flex-shrink-0 px-6 py-1.5 border-t border-gray-100 bg-white">
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    pageSizeOptions={['10', '100', '200', '500', '1000']}
                    showQuickJumper={false}
                />
            </div>

            {/* 底部操作栏 - 固定 */}
            <div className="flex-shrink-0 px-6 py-1.5 border-t border-gray-200 bg-white">
                <div className="flex justify-end space-x-2">
                    <Button
                        type="primary"
                        onClick={handleConfirmAppointment}
                        loading={confirmLoading}
                        disabled={isPastDate || filters.confirm !== 0 || selectedRowKeys.length === 0}
                        hidden={true}
                        className="w-auto min-w-24"
                    >
                        Confirm Appointment
                    </Button>
                    <Button
                        disabled={isPastDate }
                        type="primary"
                        onClick={handleCreateAppointment}
                        className="w-auto min-w-24"
                    >
                        {t('pickup.appointment.buttons.createAppointment')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleAddTask}
                        disabled={isPastDate ||filters.confirm === 0 || selectedRowKeys.length === 0 || validationLoading }
                        loading={validationLoading}
                        className="w-auto min-w-24"
                    >
                        {t('pickup.appointment.buttons.addTask')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleCreateTask}
                        disabled={isPastDate ||filters.confirm === 0 || selectedRowKeys.length === 0 || validationLoading }
                        loading={validationLoading}
                        className="w-auto min-w-24"
                    >
                        {t('pickup.appointment.buttons.createTask')}
                    </Button>
                    <Button
                        type="primary"
                        hidden={true}
                        onClick={handleMergeAppointment}
                        disabled={isPastDate ||selectedRowKeys.length < 2 ||
                            (selectedRowKeys.length >= 2 && Array.from(selectedAppointments.values()).some(appointment => appointment.status !== 'CREATED'))}
                        className="w-auto min-w-24"
                    >
                        Merge Appointment
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleCopyAppointment}
                        hidden={true}
                        className="w-auto min-w-24"
                    >
                        Copy Appointment
                    </Button>
                </div>
            </div>

            {/* 筛选条件弹窗 */}
            <Modal
                open={filterModalVisible}
                onOk={handleFilter}
                onCancel={handleFilterCancel}
                width={1000}
                okText="Apply"
                cancelText="Cancel"
                centered
                footer={[
                    <Button key="reset" onClick={handleResetFilters}>
                        {t('pickup.appointment.buttons.reset')}
                    </Button>,
                    <Button key="cancel" onClick={handleFilterCancel} hidden={true}>
                        {t('pickup.appointment.buttons.cancel')}
                    </Button>,
                    <Button key="apply" type="primary" onClick={handleFilter} className="w-auto min-w-24">
                        {t('pickup.appointment.buttons.filter')}
                    </Button>,
                ]}
            >
                <div style={{ marginTop: '30px' }} >
                    <PickupDataProvider>
                        <PickupAppointmentFilter
                            filters={filters}
                            setFilters={setFilters}
                            handleFilter={handleFilter}
                            handleResetFilters={handleResetFilters}
                            tempFilters={tempFilters}
                            setTempFilters={setTempFilters}
                            disableStatus={showUnscheduledOnly}
                            hideButtons={true}
                            disablePickupDate={true}
                        />
                    </PickupDataProvider>
                </div>

            </Modal>

            {/* 预约单详情抽屉组件 */}
            <PickupAppointmentDrawer
                visible={detailVisible}
                onClose={() => setDetailVisible(false)}
                appointmentId={appointmentId}
                mode={drawerMode}
                onSuccess={handleDrawerSuccess}
                listPickupDate={filters.expectedPickupDate}
                canCreateAppointment={canCreateAppointment}
            />

            {/* 转移任务模态框 */}
            <PickupTransferTaskModal
                visible={transferTaskModalVisible}
                onClose={() => setTransferTaskModalVisible(false)}
                onConfirm={handleTransferConfirm}
                currentTaskId={currentTaskId}
                warehouseId={warehouseId ? Number(warehouseId) : 0}
                taskDate={filters.expectedPickupDate || dayjs().format('YYYY-MM-DD')}
            />

            {/* 创建任务抽屉 */}
            <PickupTaskCreateDrawer
                visible={taskCreateDrawerVisible}
                onClose={() => setTaskCreateDrawerVisible(false)}
                onSuccess={handleTaskCreateSuccess}
                useSystemRouting={useSystemRouting}
                selectedAppointmentIds={((): number[] => {
                    // 按照拖拽后的顺序传递ID
                    // 优先使用 displayData 中选中项的顺序（包含拖拽后的顺序）
                    const selectedSet = new Set(selectedRowKeys);
                    const orderedIds: number[] = [];

                    // 从 displayData 中按照顺序收集选中项的ID
                    displayData.forEach((appointment) => {
                        if (selectedSet.has(appointment.id)) {
                            orderedIds.push(appointment.id);
                        }
                    });

                    // 如果 displayData 中没有找到所有选中项，则使用 selectionOrder 或 selectedRowKeys 作为后备
                    if (orderedIds.length !== selectedRowKeys.length) {
                        console.warn('⚠️ 部分选中项在 displayData 中未找到，使用 selectionOrder 或 selectedRowKeys 作为后备');
                        return selectionOrder.length > 0
                            ? selectionOrder.map((key: React.Key) => Number(key))
                            : selectedRowKeys.map((key: React.Key) => Number(key));
                    }

                    console.log(`✅ 创建任务 - 按照拖拽顺序传递 ${orderedIds.length} 个预约单ID:`, orderedIds);
                    return orderedIds;
                })()}
                pickupDate={getCurrentPickupDate()}
            taskSource={PickupSourceEnum.APPOINTMENT_MAP}
            />

            {/* 任务详情抽屉 */}
            {taskDetailDrawerVisible && (
                <PickupDataProvider>
                    <PickupTaskDetailDrawer
                        visible={taskDetailDrawerVisible}
                        onClose={() => setTaskDetailDrawerVisible(false)}
                        taskId={currentTaskId}
                        onSave={handleTaskDetailSuccess}
                    />
                </PickupDataProvider>
            )}

            {/* 合并预约模态框 */}
            <PickupMergeAppointmentModal
                visible={mergeAppointmentModalVisible}
                onClose={() => setMergeAppointmentModalVisible(false)}
                onSuccess={handleMergeSuccess}
                selectedAppointments={Array.from(selectedAppointments.values())}
                pickupDate={filters.expectedPickupDate || dayjs().format('YYYY-MM-DD')}
            />

            {/* 复制预约模态框 */}
            <PickupAppointmentCopyModal
                visible={copyAppointmentModalVisible}
                onClose={() => setCopyAppointmentModalVisible(false)}
                onSuccess={handleCopySuccess}
                listPickupDate={filters.expectedPickupDate}
            />

            <AppointmentDownloadModal
                visible={appointmentDownloadVisible}
                onClose={() => setAppointmentDownloadVisible(false)}
                fileName={appointmentFileName}
            />
        </div>
    );
};

export default PickupAppointmentsContent;
